import { getPageRoutes } from "@/routes/pageRoutes";
import {
  isShowBookable,
  isShowViewable,
  sortAdvanceBookingFirst,
} from "@picovico/ui/constant/enums";
import { useAppSelector } from "@/store";
import {
  useGetComingSoonQuery,
  useGetNextReleaseQuery,
  useGetNowShowingQuery,
} from "@/store/services/movie";
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import {
  AlertMessage,
  DateSelectorWithView,
  MovieCardAsGallery,
  MovieCardAsList,
  MovieCardListSkeleton,
  MovieTabProps,
} from "@picovico/ui";
import { NO_MOVIE } from "@picovico/ui/constant/alertMessage";
import {
  convertMinutesToHours,
  convertTimeTo12HourFormat,
  getLocalStorageData,
} from "@picovico/utils";
import { format } from "date-fns";
import React, { useState } from "react";

function NowShowingWithGalleryAndList({
  mainBoxProps,
  tabsProps,
  tabListProps,
  tabPanelsProps,
  tabPanelProps,
  tabProps,
  prioritizeAdvanceBooking = false,
}: Omit<MovieTabProps, "tabs"> & {
  /** List advance-booking movies first in the Now Showing tab. */
  prioritizeAdvanceBooking?: boolean;
}) {
  const now = new Date().toLocaleString();
  const [date, setDate] = useState<string>(now);
  const [view, setView] = useState<boolean>(true);
  const { company_idx } = useAppSelector((state) => state.config);
  const { cinema_hall } = useAppSelector((state) => state.activeCinemaHall);
  const { isLoading, data, isFetching } = useGetNowShowingQuery(
    {
      show_date: format(date, "yyyy-MM-dd"),
      cinema_hall: cinema_hall,
    },
    {
      skip: !cinema_hall,
    },
  );

  const {
    isFetching: isNextReleaseFetching,
    isLoading: isNextReleaseLoading,
    data: nextRelease,
  } = useGetNextReleaseQuery(
    { company_idx, cinema_hall },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );
  const { data: comingSoon, isLoading: isLoadingComingSoon } =
    useGetComingSoonQuery({
      company_idx,
      cinema_hall: cinema_hall ?? getLocalStorageData("cinema_hall"),
    });

  const upcomingMovies = [
    ...(nextRelease?.data || []),
    ...(comingSoon?.data || []),
  ]?.map(
    ({
      idx,
      name,
      release_date,
      grade,
      roll_quality,
      genre,
      poster,
      duration,
      trailer,
      movieinfo,
    }) => ({
      idx,
      title: name,
      grade,
      roll_quality,
      genre,
      poster,
      detailLink: getPageRoutes(
        "movie_detail",
        {
          movieIdx: idx,
          cinemaHallIdx: cinema_hall ?? getLocalStorageData("cinema_hall"),
        },
        {
          startDate: movieinfo?.show_start_date,
          endDate: movieinfo?.show_end_date,
        },
      ),
      duration: convertMinutesToHours(duration),
      releaseDate: format(new Date(release_date), "MMMM d yyyy"),
      videoLink: trailer,
      isNowShowing: Boolean(movieinfo?.show_start_date),
    }),
  );

  const nowShowingMovies =
    prioritizeAdvanceBooking && data?.data
      ? sortAdvanceBookingFirst(data.data, (m) => new Date(m.release_date))
      : data?.data;

  const movies =
    nowShowingMovies?.map(
      ({
        idx,
        grade,
        duration,
        poster,
        showinfo,
        release_date,
        genre,
        ...item
      }) => ({
        title: item.name,
        poster,
        grade,
        genre,
        duration: convertMinutesToHours(duration),
        idx,
        releaseDate: format(new Date(release_date), "MMMM d yyyy"),
        videoLink: item.trailer,
        detailLink: getPageRoutes(
          "movie_detail",
          {
            movieIdx: idx,
            cinemaHallIdx: cinema_hall as string,
          },
          {
            date: date,
            startDate: item.movieinfo?.show_start_date,
            endDate: item.movieinfo?.show_end_date,
          },
        ),
        movieShowTimes:
          showinfo?.map((i) => ({
            ...i,
            start_time: convertTimeTo12HourFormat(
              i.show_start_date_time.slice(11, 16),
            ),
            available: isShowBookable(i.show_status),
            viewable: isShowViewable(i.show_status, i.show_ended, i.seat_view_allowed),
            showLink: getPageRoutes("audi_detail", {
              movieIdx: idx,
              showIdx: i.show_idx,
            }),
          })) || [],
        isNowShowing: true,
      }),
    ) || [];

  return (
    <Box mt={{ base: 2, md: 3, lg: 4 }} px={{ base: 2, md: 4, lg: 0 }}>
      <Box {...mainBoxProps}>
        <Tabs
          variant="unstyled"
          display="flex"
          flexDirection="column"
          alignItems="center"
          {...tabsProps}
        >
          <TabList
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "surface.500",
              color: "white",
              rounded: "full",
              ...(tabListProps?.sx || {}),
            }}
            {...tabListProps}
          >
            <Tab
              sx={{
                rounded: "full",
                transition: "all 0.3s ease",
                fontWeight: "bold",
              }}
              _selected={{
                background: "primary.500",
              }}
              px={{ base: "1rem", md: "1.5rem", xl: "2rem" }}
              py={{
                base: "0.5rem",
                md: "0.75rem",
                xl: "1rem",
              }}
              key={`movie__tab__gird__tab+${0}`}
              fontSize={{
                base: "14px",
                md: "18px",
                xl: "26px",
              }}
              {...tabProps}
            >
              Now Showing
            </Tab>
            <Tab
              sx={{
                rounded: "full",
                transition: "all 0.3s ease",
                fontWeight: "bold",
              }}
              _selected={{
                background: "primary.500",
              }}
              px={{ base: "1rem", md: "1.5rem", xl: "2rem" }}
              py={{
                base: "0.5rem",
                md: "0.75rem",
                xl: "1rem",
              }}
              key={`movie__tab__gird__tab+${1}`}
              fontSize={{
                base: "14px",
                md: "18px",
                xl: "26px",
              }}
              {...tabProps}
            >
              Upcoming Movies
            </Tab>
          </TabList>
          <TabPanels w="100%" {...tabPanelsProps}>
            <TabPanel
              key={`tab__pane__${1}`}
              px={{ base: 0, md: 4 }}
              {...tabPanelProps}
            >
              <Box>
                <DateSelectorWithView
                  date={date}
                  view={view}
                  totalResults={data?.data.length}
                  handleViewChange={(val) => {
                    setView(val);
                  }}
                  handleDateChange={(val) => {
                    setDate(val);
                  }}
                />
                {isLoading || isFetching ? (
                  <MovieCardListSkeleton />
                ) : !data?.data || data?.data?.length === 0 ? (
                  <AlertMessage
                    message={NO_MOVIE}
                    sx={{
                      mt: { base: "0.5rem", md: "1rem" },
                    }}
                  />
                ) : (
                  <Flex
                    wrap="wrap"
                    gap={{ base: 2, md: 4 }}
                    justifyContent="flex-start"
                    w="100%"
                    mt={{ base: 2, md: 4 }}
                  >
                    {movies.map((movie) =>
                      view ? (
                        <MovieCardAsGallery key={movie.idx} {...movie} />
                      ) : (
                        <MovieCardAsList key={movie.idx} {...movie} />
                      ),
                    )}
                  </Flex>
                )}
              </Box>
            </TabPanel>
            <TabPanel
              key={`tab__pane__${2}`}
              px={{ base: 0, md: 4 }}
              {...tabPanelProps}
            >
              {isNextReleaseLoading ||
              isLoadingComingSoon ||
              isNextReleaseFetching ? (
                <MovieCardListSkeleton />
              ) : !upcomingMovies || upcomingMovies?.length === 0 ? (
                <AlertMessage
                  message={NO_MOVIE}
                  sx={{
                    mt: { base: "0.5rem", md: "1rem" },
                  }}
                />
              ) : (
                <Flex
                  wrap="wrap"
                  gap={{ base: 2, md: 3, lg: 4 }}
                  mt={{ base: 3, md: 4, lg: 6 }}
                  justifyContent={{ base: "center", md: "flex-start" }}
                  w="100%"
                >
                  {upcomingMovies.map((movie) => (
                    <MovieCardAsGallery key={movie.idx} {...movie} />
                  ))}
                </Flex>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}

export default NowShowingWithGalleryAndList;
